import { createClient } from "@supabase/supabase-js";

// Support both Vite (VITE_) and Next.js / universal (NEXT_PUBLIC_) prefixes
const rawSupabaseUrl = (import.meta.env.VITE_SUPABASE_URL || import.meta.env.NEXT_PUBLIC_SUPABASE_URL || "") as string;
const rawAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "") as string;

export const supabaseUrl = rawSupabaseUrl ? rawSupabaseUrl.trim().replace(/\/+$/, "") : "https://placeholder.supabase.co";
export const supabaseAnonKey = rawAnonKey ? rawAnonKey.trim() : "placeholder-anon-key";

// DEBUG DIAGNOSTICS: Check if variables are indeed loading
if (typeof window !== "undefined") {
  console.log("Supabase URL Check:", !!rawSupabaseUrl, rawSupabaseUrl ? `(${new URL(supabaseUrl).hostname})` : "(empty)");
  console.log("Supabase Anon Key Check:", !!rawAnonKey);
}

if (!rawSupabaseUrl || !rawAnonKey) {
  console.error(
    "CRITICAL: Supabase environment variables are missing! " +
    "Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY) " +
    "are set in your .env.local file and RESTART your dev server."
  );
}

// 1. Standard Client (Used for all public/user-level auth)
export const supabase = (globalThis as any).__supabaseClient || createClient(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'ib-meridian-auth', // Custom key to avoid collisions
      flowType: 'pkce'
    },
  }
);
if (!(globalThis as any).__supabaseClient) {
  (globalThis as any).__supabaseClient = supabase;
}

// NOTE: There is intentionally NO service-role client in the browser.
// Privileged operations (create/delete users, delete subjects) go through the
// admin-checked Edge Functions in supabase/functions/*-admin.

export type UserRole = "student" | "teacher" | "admin";

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  credit_hours: number;
  phone?: string;
  avatar_url?: string;
  bio?: string;
  subjects?: string[];
  subscription_tier?: 'Free' | 'Premium' | 'Elite' | string;
  years_experience?: number;
  specialization?: string;
  credentials?: string;
  session_price?: number;
  created_at: string;
}

export interface AcademicResource {
  id: string;
  title: string;
  subject: string;
  description?: string;
  file_url: string;
  file_type: string;
  file_size?: number;
  resource_type?: "note" | "paper" | "lesson-plan" | "other";
  level?: "HL" | "SL" | "Both";
  uploaded_by?: string;
  is_visible?: boolean;
  audience?: string;
  created_at: string;
}

export interface Enrollment {
  id: string;
  student_id: string;
  teacher_id: string;
  subject: string;
  status: "active" | "paused" | "completed";
  created_at: string;
  student?: Profile;
  teacher?: Profile;
}

export interface Testimonial {
  id: string;
  created_at: string;
  student_id: string;
  teacher_id?: string;
  course_id?: string;
  testimonial_text: string;
  rating: number;
  student_image_url?: string;
  video_url?: string;
  university?: string | null;
  grade_before?: string | null;
  grade_after?: string | null;
  is_approved: boolean;
  is_manual_entry: boolean;
  student?: Profile;
  teacher?: Profile;
}

/**
 * Standard utility for role-based redirection to ensure consistency across pages.
 */
export function getRoleDashboard(role: UserRole | string): string {
  switch (role) {
    case "admin":   return "/admin";
    case "teacher": return "/teacher/portal";
    case "student": return "/student/portal";
    default:        return "/student/portal";
  }
}
